/**
 * Platform settings — FPS staff only.
 *
 * Front Porch's own values rather than a client's. Client branding lives on
 * the client record and is edited from there.
 */

import { t } from '../lib/copy';
import { T } from '../lib/copyEdit.tsx';
import { Link } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import EmailTemplateEditor, {
  type EmailTemplateMeta,
  type FieldInfo,
  type TemplateDraft,
} from '../components/EmailTemplateEditor.tsx';

interface Settings {
  privacy_policy_url: string | null;
  terms_of_service_url: string | null;
  access_contact_name: string | null;
  access_contact_email: string | null;
  welcome_subject: string | null;
  welcome_heading: string | null;
  welcome_intro: string | null;
  welcome_staff_subject: string | null;
  welcome_staff_heading: string | null;
  welcome_staff_intro: string | null;
  reminder_send_hour: string | null;
  reminder_skip_weekends: string | null;
  notify_email: string | null;
  notify_mode: string | null;
  message_templates: string | null;
}

interface Template {
  name: string;
  subject: string;
  body: string;
}

/** One machine-sent template as the payload carries it: the prose always, and
 *  the repeated lines only where somebody has edited one. */
type TemplateEntry = [string, { subject: string; body: string; lines?: Record<string, string> }];

/* Loaded INTO the fields, not shown behind them.
 *
 * Editing an existing sentence is a much smaller task than writing one from
 * a blank box, and these are long enough that starting empty means most
 * people never touch them. The server keeps the same copy as a fallback, so
 * clearing a field still sends the default rather than nothing. */
const DEFAULTS = {
  welcome_subject: 'Your Front Porch Solutions account is ready',
  welcome_heading: 'Welcome to Sponsor Logos',
  welcome_intro:
    'Front Porch Solutions has set up your account for the application used to collect artwork from conference sponsors.',
  welcome_staff_subject: 'Your Front Porch Solutions admin access is ready',
  welcome_staff_heading: 'Admin access to Sponsor Logos',
  welcome_staff_intro:
    'Front Porch Solutions has given you admin access to Sponsor Logos. You can see every client and everything under them.',
};

/**
 * The sections of this screen, in order, each owning the settings it writes.
 *
 * ONE DESCRIPTION, used three times: to draw the rail, to decide which section
 * is showing, and to work out what a section's save should send. Previously
 * the page was one form with one save, so a section was only a visual
 * grouping; now that each saves independently a section has to know what it
 * contains, and this is the only place that says so.
 *
 * `keys` must stay in step with the payload builder. A key in neither is a
 * field that silently never saves, which is the failure this arrangement
 * exists to make hard: settingsSections.test.ts asserts every key the payload
 * produces is claimed by exactly one section.
 */
interface Section {
  id: string;
  /** What the rail calls it. */
  nav: string;
  /** The heading inside the pane, where there is room for more words. */
  title: string;
  group: string;
  keys: string[];
}

const SECTIONS: Section[] = [
  {
    id: 'platform',
    nav: 'Platform',
    title: 'Platform',
    group: 'Configuration',
    keys: ['privacy_policy_url', 'terms_of_service_url'],
  },
  {
    id: 'access',
    nav: 'Access',
    title: 'Who to Ask for Access',
    group: 'Configuration',
    keys: ['access_contact_name', 'access_contact_email'],
  },
  {
    id: 'reminders',
    nav: 'Reminders',
    title: 'Sponsor Reminders',
    group: 'Configuration',
    keys: ['reminder_send_hour', 'reminder_skip_weekends'],
  },
  {
    id: 'notifications',
    nav: 'Notifications',
    title: 'Notifications to Us',
    group: 'Configuration',
    keys: ['notify_email', 'notify_mode'],
  },
  /* One section per automatic email rather than one for all three. Each
     carries a subject, a body, a field menu, a validation line and a preview,
     so three of them in a single pane is the long scroll this screen is
     getting away from. */
  {
    id: 'template_invite',
    nav: 'Invitation',
    title: 'Invitation',
    group: 'Automatic emails',
    keys: ['template_invite'],
  },
  {
    id: 'template_reminder',
    nav: 'Reminder',
    title: 'Reminder',
    group: 'Automatic emails',
    keys: ['template_reminder'],
  },
  {
    id: 'template_link_replaced',
    nav: 'New Upload Link',
    title: 'New Upload Link',
    group: 'Automatic emails',
    keys: ['template_link_replaced'],
  },
  /* The review is two sections because it is two messages. One asks the sponsor
     for work and the other confirms there is none, and editing them together
     would mean every change to the good-news wording risked the other. */
  {
    id: 'template_review_changes',
    nav: 'Review: Changes',
    title: 'Review: Changes Requested',
    group: 'Automatic emails',
    keys: ['template_review_changes'],
  },
  {
    id: 'template_review_approved',
    nav: 'Review: Approved',
    title: 'Review: Approved',
    group: 'Automatic emails',
    keys: ['template_review_approved'],
  },
  {
    id: 'messages',
    nav: 'Message Templates',
    title: 'Message Templates',
    group: 'Wording',
    keys: ['message_templates'],
  },
  {
    id: 'welcome',
    nav: 'Welcome Email',
    title: 'Welcome Email',
    group: 'Wording',
    keys: [
      'welcome_subject',
      'welcome_heading',
      'welcome_intro',
      'welcome_staff_subject',
      'welcome_staff_heading',
      'welcome_staff_intro',
    ],
  },
];

export default function Settings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [welcome, setWelcome] = useState<Record<string, string>>({});
  const [sendHour, setSendHour] = useState('9');
  const [notifyEmail, setNotifyEmail] = useState('');
  /* Off until somebody turns it on: half of these reach the client. */
  const [notifyMode, setNotifyMode] = useState('off');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  /* The wording of the emails the app sends on its own. The registry comes
     from the server, which is also what sends them, so which fields resolve
     where cannot drift between the editor and the send path. */
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateMeta[]>([]);
  const [fields, setFields] = useState<Record<string, FieldInfo>>({});
  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({});
  const [privacy, setPrivacy] = useState('');
  const [terms, setTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  /** Which section the pane is showing. */
  const [active, setActive] = useState<string>(SECTIONS[0]!.id);
  /**
   * The values as they were loaded or last written.
   *
   * What "unsaved" is measured against. Null until the first load, which is
   * why isDirty answers false then: a page that has not finished loading has
   * not been edited.
   */
  const [baseline, setBaseline] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/settings');
      if (!res.ok) {
        setMessage('Could not load settings.');
        return;
      }
      const body = (await res.json()) as {
        settings: Settings;
        emailTemplates?: EmailTemplateMeta[];
        fields?: Record<string, FieldInfo>;
      };
      setSettings(body.settings);
      setPrivacy(body.settings.privacy_policy_url ?? '');
      setTerms(body.settings.terms_of_service_url ?? '');
      setContactName(body.settings.access_contact_name ?? '');
      setContactEmail(body.settings.access_contact_email ?? '');
      /* Stored value where there is one, the shipped copy otherwise — so the
         fields always arrive with something to edit. */
      const stored = body.settings as unknown as Record<string, string | null>;
      setSendHour(body.settings.reminder_send_hour?.trim() || '9');
      setNotifyEmail(body.settings.notify_email ?? '');
      setNotifyMode(body.settings.notify_mode?.trim() || 'off');
      setSkipWeekends(body.settings.reminder_skip_weekends !== 'false');
      /* Falls back to whatever the server sends rather than throwing: a
         malformed row must not break the only screen that can fix it. */
      try {
        const parsed = JSON.parse(body.settings.message_templates || '[]') as Template[];
        setTemplates(Array.isArray(parsed) ? parsed : []);
      } catch {
        setTemplates([]);
      }
      const machineTemplates = body.emailTemplates ?? [];
      setEmailTemplates(machineTemplates);
      setFields(body.fields ?? {});
      setDrafts(
        Object.fromEntries(
          machineTemplates.map((t) => [
            t.key,
            {
              subject: t.subject,
              body: t.body,
              /* Only where the template has any. An absent `lines` on a draft
                 means "the shipped ones", which is how every template without
                 repeated lines stays exactly as it was. */
              ...(t.lineMeta?.length ? { lines: { ...(t.lines ?? {}) } } : {}),
            },
          ]),
        ),
      );
      setWelcome(
        Object.fromEntries(
          Object.entries(DEFAULTS).map(([k, fallback]) => [k, stored[k]?.trim() || fallback]),
        ),
      );
    })();
  }, []);

  /**
   * Records what was loaded, once, as the thing "unsaved" is measured against.
   *
   * Taken from payload() rather than assembled from the response, so the
   * baseline is in exactly the shape a save produces: trimmed, normalised, and
   * carrying the email templates only when they already differ from the
   * shipped wording. Built any other way, the page would come up claiming
   * changes nobody had made.
   *
   * Runs on `settings` becoming set, by which point the load has applied every
   * other piece of state in the same batch.
   */
  useEffect(() => {
    if (settings && !baseline) setBaseline(payload());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload reads the
    // state this effect is waiting for; depending on it would re-run forever.
  }, [settings, baseline]);

  /**
   * Everything this screen holds, as the API wants it.
   *
   * Built in one place so that saving a section and working out whether a
   * section has changed cannot disagree about what a section contains. They
   * used to be the same thing because there was only one save; now that there
   * are eight, a second copy of this would be a second place to forget a key.
   */
  function payload(): Record<string, unknown> {
    return {
      privacy_policy_url: privacy.trim(),
      terms_of_service_url: terms.trim(),
      access_contact_name: contactName.trim(),
      access_contact_email: contactEmail.trim(),
      ...Object.fromEntries(Object.entries(welcome).map(([k, v]) => [k, v.trim()])),
      reminder_send_hour: sendHour,
      reminder_skip_weekends: skipWeekends ? 'true' : 'false',
      notify_email: notifyEmail.trim(),
      notify_mode: notifyMode,
      message_templates: templates.filter((t) => t.name.trim() && t.body.trim()),
      /* Only the ones actually changed, and within those only the lines
         actually changed. Sending every template back would write the shipped
         wording into the database on any save, freezing it against later edits
         to the defaults — and the same is true a line at a time, which is why
         the untouched ones are filtered out rather than sent along. */
      ...Object.fromEntries(
        emailTemplates
          .map((t): TemplateEntry | null => {
            const d = drafts[t.key];
            if (!d) return null;

            const changedLines = Object.fromEntries(
              (t.lineMeta ?? [])
                .filter((l) => (d.lines?.[l.key] ?? '') !== (t.shippedLines?.[l.key] ?? ''))
                .map((l) => [l.key, d.lines?.[l.key] ?? '']),
            );
            const prose = d.subject !== t.shippedSubject || d.body !== t.shippedBody;
            if (!prose && Object.keys(changedLines).length === 0) return null;

            return [
              t.key,
              {
                subject: d.subject,
                body: d.body,
                ...(Object.keys(changedLines).length > 0 ? { lines: changedLines } : {}),
              },
            ];
          })
          .filter((entry): entry is TemplateEntry => entry !== null),
      ),
    };
  }

  /**
   * Whether a section holds anything not yet written.
   *
   * DERIVED BY COMPARISON rather than tracked with a flag. A flag has to be
   * set on every change and cleared on every save, and the bug that follows is
   * always the same: one input nobody wired up, so a section looks clean while
   * holding an edit. Comparing against what was loaded cannot drift, and it
   * also means undoing a change by hand correctly marks the section clean
   * again.
   */
  function isDirty(section: Section): boolean {
    if (!baseline) return false;
    const now = payload();
    return section.keys.some((k) => JSON.stringify(now[k]) !== JSON.stringify(baseline[k]));
  }

  const dirtySections = baseline ? SECTIONS.filter(isDirty) : [];

  /**
   * Writes one section, or everything that has changed.
   *
   * The endpoint has always taken a partial update, so this needed no server
   * work: a body with two keys in it writes two settings and leaves the rest
   * alone.
   */
  async function saveSections(which: Section[]) {
    if (which.length === 0) return;
    setSaving(true);
    setStatus('idle');
    setMessage(null);

    const all = payload();
    const keys = new Set(which.flatMap((s) => s.keys));
    const body = Object.fromEntries(Object.entries(all).filter(([k]) => keys.has(k)));

    const res = await fetch('/api/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    setSaving(false);

    if (res.ok) {
      const next = (await res.json()) as { settings: Settings };
      setSettings(next.settings);
      /* Only the saved keys move in the baseline. Anything still unsaved in
         another section stays marked, which is the whole point of saving one
         at a time. */
      setBaseline((prev) => ({ ...(prev ?? {}), ...body }));
      setStatus('saved');
      setMessage(
        which.some((s) => s.id === 'platform')
          ? 'Saved. Reload to see the footer links update.'
          : 'Saved.',
      );
    } else {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      setStatus('error');
      setMessage(err.error ?? 'That did not save.');
    }
  }

  if (!settings && !message) return <div className="app-loading" aria-busy="true" />;

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow">Front Porch Solutions</p>
          <h1 className="title"><T k="settings.title" d="Settings" /></h1>
          <p className="lede">
            <T
              k="settings.lede"
              d="Platform configuration. These apply across every client, so they are managed by Front Porch rather than per organization."
            />
          </p>
        </div>
        {/* The one platform surface that is a working session rather than a
            form, so it gets a way in from here rather than living at a URL
            somebody has to be told about. */}
        <div className="pactions">
          <Link className="btn-compact btn-compact--quiet" to="/settings/text">
            <T k="settings.action.editText" d="Edit Application Text" />
          </Link>
        </div>
      </div>

      {/* A RAIL AND ONE PANE, rather than one scroll with everything in it.
          Three of these sections are email templates carrying a subject, a
          body, a field menu, a validation line and a preview, so the page was
          long enough that the save button at the bottom of it was out of sight
          from most of the fields it applied to.

          Each section writes only its own settings, which the endpoint has
          always allowed. The rail carries a dot on anything unsaved, because
          one-at-a-time means a change you have made can be behind a menu item
          you are not looking at. */}
      <div className="setgrid">
        <nav className="setrail" aria-label="Settings sections">
          {SECTIONS.map((section, i) => {
            const newGroup = i === 0 || SECTIONS[i - 1]!.group !== section.group;
            return (
              <Fragment key={section.id}>
                {newGroup && <p className="setrail-group">{section.group}</p>}
                <button
                  type="button"
                  className={`setrail-item${active === section.id ? ' on' : ''}${
                    isDirty(section) ? ' dirty' : ''
                  }`}
                  aria-current={active === section.id}
                  onClick={() => setActive(section.id)}
                >
                  <span className="setrail-dot" aria-hidden="true" />
                  {section.nav}
                  {isDirty(section) && <span className="visually-hidden"> (unsaved changes)</span>}
                </button>
              </Fragment>
            );
          })}
        </nav>

        <div className="setpane">

          {active === 'platform' && (
            <section className="setsect">
              <h3 className="setsect-title">Platform</h3>
              <div className="fg">
            <label className="fl" htmlFor="privacy"><T k="settings.field.privacy" d="Privacy Policy URL" /></label>
            <input
              id="privacy"
              type="url"
              className="fi"
              placeholder="https://frontporchsolutions.com/privacy-policy"
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
            />
          </div>

          <div className="fg">
            <label className="fl" htmlFor="terms"><T k="settings.field.terms" d="Terms of Service URL" /></label>
            <input
              id="terms"
              type="url"
              className="fi"
              placeholder="https://frontporchsolutions.com/terms-of-service"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
            />
          </div>

          <p className="fdesc">
            Each link only appears once its address is set. Leave a field empty to hide that link
            rather than pointing people at a page that does not exist.
          </p>
              <SectionSave
                section={SECTIONS.find((s) => s.id === 'platform')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'platform')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

          {active === 'access' && (
            <section className="setsect">
              <h3 className="setsect-title">Who to Ask for Access</h3>
              {/* Sign-up is disabled, so anyone not provisioned reaches a dead end
              unless there is a name here. Kept in settings rather than the
              source because who to ask changes with staffing. */}
          <p className="fl" style={{ marginBottom: 4 }}>Who to Ask for Access</p>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 14 }}>
            Shown to anyone who signs in successfully but has no account yet. Without this they are
            told only that access is arranged by Front Porch Solutions, with nobody to contact.
          </p>

          <div className="frow2">
            <div className="fg">
              <label className="fl" htmlFor="contactName"><T k="settings.field.contactName" d="Contact Name" /></label>
              <input
                id="contactName"
                className="fi"
                maxLength={160}
                placeholder="Kregg Kish"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className="fg">
              <label className="fl" htmlFor="contactEmail"><T k="common.field.contactEmail" d="Contact Email" /></label>
              <input
                id="contactEmail"
                type="email"
                className="fi"
                maxLength={320}
                placeholder="k.kish@frontporchsolutions.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <p className="fdesc"><T k="settings.field.contactEmailHint" d="Turns the name into a mailto link. Optional." /></p>
            </div>
          </div>
              <SectionSave
                section={SECTIONS.find((s) => s.id === 'access')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'access')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

          {active === 'reminders' && (
            <section className="setsect">
              <h3 className="setsect-title">Sponsor Reminders</h3>
              {/* A DEFAULT, not the only setting. Each client may override both of
              these on their own record, and one that has is unaffected by
              changes here. The timezone is neither: it comes from each event,
              so nine o'clock means nine o'clock where the conference is
              rather than where the server is. */}
          <p className="fl" style={{ marginBottom: 4 }}>Sponsor Reminders</p>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 14 }}>
            A sponsor hears from us when they are invited, then three weeks, one week, three days
            and one day before a logo deadline, and again on the day. These are the defaults for
            when each reminder sends, in the event's own timezone. A client can set its own on the
            Clients screen, and changing these will not affect one that has.
          </p>

          <div className="frow2">
            <div className="fg">
              <label className="fl" htmlFor="sendHour"><T k="settings.field.sendAt" d="Send At" /></label>
              <select
                id="sendHour"
                className="fi"
                value={sendHour}
                onChange={(e) => setSendHour(e.target.value)}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={String(h)}>
                    {h === 0 ? '12:00 am' : h < 12 ? `${h}:00 am` : h === 12 ? '12:00 pm' : `${h - 12}:00 pm`}
                  </option>
                ))}
              </select>
              <p className="fdesc">
                In each event's timezone. Early morning reads as automated; mid-morning reads like
                someone noticed.
              </p>
            </div>

            <div className="fg">
              <label className="fl"><T k="settings.field.weekends" d="Weekends" /></label>
              <label className="fcheck" style={{ marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={(e) => setSkipWeekends(e.target.checked)}
                />
                <span>
                  Do not send on weekends
                  <span className="fdesc">
                    A Saturday reminder is usually gone by Monday. Turn this off if a deadline could
                    fall early in the week.
                  </span>
                </span>
              </label>
            </div>
          </div>
              <SectionSave
                section={SECTIONS.find((s) => s.id === 'reminders')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'reminders')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

          {active === 'notifications' && (
            <section className="setsect">
              <h3 className="setsect-title"><T k="settings.notify.title" d="Notifications to Us" /></h3>
              <p className="fdesc" style={{ marginTop: 0, marginBottom: 14 }}>
                <T
                  k="settings.notify.intro"
                  d="Where Front Porch hears about sponsor activity: artwork arriving, a sponsor confirming the logo we already hold, an email bouncing, a file refused by the security scan, and a deadline passing with versions still missing. Each client also gets the ones about their own sponsors, at their reply-to address."
                />
              </p>

              <div className="fg">
                <label className="fl" htmlFor="notifyEmail">
                  <T k="settings.field.notifyEmail" d="Front Porch Address" />
                </label>
                <input
                  id="notifyEmail"
                  className="fi"
                  maxLength={640}
                  placeholder="info@frontporchsolutions.com"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                />
                <p className="fdesc">
                  <T
                    k="settings.field.notifyEmailHint"
                    d="Separate several with commas. Leave it empty and Front Porch is not copied at all; clients still get theirs."
                  />
                </p>
              </div>

              <div className="fg">
                <label className="fl" htmlFor="notifyMode">
                  <T k="settings.field.notifyMode" d="Send Them" />
                </label>
                <select
                  id="notifyMode"
                  className="fi"
                  value={notifyMode}
                  onChange={(e) => setNotifyMode(e.target.value)}
                >
                  <option value="immediate">{t('settings.notify.immediate', 'As things happen')}</option>
                  <option value="off">{t('settings.notify.off', 'Do not send any')}</option>
                </select>
                <p className="fdesc">
                  <T
                    k="settings.field.notifyModeHint"
                    d="These are off until you switch them on, because half of them reach the client at their reply-to address. A sponsor who uploads four versions sends four emails."
                  />
                </p>
              </div>

              <SectionSave
                section={SECTIONS.find((s) => s.id === 'notifications')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'notifications')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

          {SECTIONS.filter((s) => s.group === 'Automatic emails').map((section) =>
            active === section.id ? (
              <section className="setsect" key={section.id}>
                <h3 className="setsect-title">{section.title}</h3>
                {emailTemplates.length === 0 ? (
                  <p className="fdesc" style={{ marginTop: 0 }}>Loading.</p>
                ) : (
                  emailTemplates
                    .filter((meta) => meta.key === section.id)
                    .map((meta) => (
                      <EmailTemplateEditor
                        key={meta.key}
                        meta={meta}
                        fields={fields}
                        draft={drafts[meta.key] ?? { subject: meta.subject, body: meta.body }}
                        onChange={(next) => setDrafts((d) => ({ ...d, [meta.key]: next }))}
                      />
                    ))
                )}
                <SectionSave
                  section={section}
                  dirty={isDirty(section)}
                  saving={saving}
                  onSave={saveSections}
                />
              </section>
            ) : null,
          )}

          {active === 'messages' && (
            <section className="setsect">
              <h3 className="setsect-title">Message Templates</h3>
              <p className="fl" style={{ marginBottom: 4 }}>Message Templates</p>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 14 }}>
            Starting points when writing to a sponsor by hand. Every one is editable before it
            sends, so these are a head start rather than a script. The same fields work here as
            in the automatic emails above, apart from the upload link: <code>{'{contact}'}</code>,{' '}
            <code>{'{company}'}</code>, <code>{'{event}'}</code>, <code>{'{deadline}'}</code>,{' '}
            <code>{'{versions}'}</code> and <code>{'{client}'}</code>. They are filled in when you
            pick a template, so you can read and edit the finished wording before it sends.
          </p>

          {templates.map((template, i) => (
            <div className="wfields" key={i}>
              <div className="wfields-head">
                <p className="wfields-title">{template.name || 'Untitled'}</p>
                <button
                  type="button"
                  className="cedit cedit--danger"
                  onClick={() => setTemplates((prev) => prev.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>

              <div className="frow2">
                <div className="fg">
                  <label className="fl">Name</label>
                  <input
                    className="fi"
                    maxLength={80}
                    value={template.name}
                    onChange={(e) =>
                      setTemplates((prev) =>
                        prev.map((t, j) => (j === i ? { ...t, name: e.target.value } : t)),
                      )
                    }
                  />
                </div>
                <div className="fg">
                  <label className="fl">Subject</label>
                  <input
                    className="fi"
                    maxLength={200}
                    value={template.subject}
                    onChange={(e) =>
                      setTemplates((prev) =>
                        prev.map((t, j) => (j === i ? { ...t, subject: e.target.value } : t)),
                      )
                    }
                  />
                </div>
              </div>

              <div className="fg">
                <label className="fl">Message</label>
                <textarea
                  className="fi ft"
                  rows={4}
                  maxLength={4000}
                  value={template.body}
                  onChange={(e) =>
                    setTemplates((prev) =>
                      prev.map((t, j) => (j === i ? { ...t, body: e.target.value } : t)),
                    )
                  }
                />
              </div>
            </div>
          ))}

          {templates.length < 12 && (
            <button
              type="button"
              className="btn-compact btn-compact--quiet"
              onClick={() =>
                setTemplates((prev) => [...prev, { name: '', subject: '', body: '' }])
              }
            >
              Add a Template
            </button>
          )}
              <SectionSave
                section={SECTIONS.find((s) => s.id === 'messages')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'messages')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

          {active === 'welcome' && (
            <section className="setsect">
              <h3 className="setsect-title">Welcome Email</h3>
              {/* Editable because a subject line and an opening sentence are the
              parts of a message that get revised, and a redeploy is the wrong
              price for changing a sentence. Empty falls back to the shipped
              wording, so clearing a field can never send a blank email. */}
          <p className="fl" style={{ marginBottom: 4 }}>Welcome Email</p>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 14 }}>
            Sent when someone is given access. These start as the standard wording, and you can
            edit any of it. What each role can do is described automatically and is not editable
            here.
          </p>

          <WelcomeFields
            title="For client admins"
            note="They sign in with an emailed link and see only their own clients."
            prefix=""
            values={welcome}
            onChange={(k, v) => setWelcome((prev) => ({ ...prev, [k]: v }))}
          />

          <WelcomeFields
            title="For Front Porch staff"
            note="They sign in with Google and see every client."
            prefix="staff_"
            values={welcome}
            onChange={(k, v) => setWelcome((prev) => ({ ...prev, [k]: v }))}
          />
              <SectionSave
                section={SECTIONS.find((s) => s.id === 'welcome')!}
                dirty={isDirty(SECTIONS.find((s) => s.id === 'welcome')!)}
                saving={saving}
                onSave={saveSections}
              />
            </section>
          )}

        </div>
      </div>

      {message && <p className={status === 'error' ? 'auth-error' : 'save-note'}>{message}</p>}

      {/* THE BACKSTOP. One section at a time means a change can sit behind a
          menu item, so something has to say so from wherever you are. */}
      {dirtySections.length > 0 && (
        <div className="setunsaved">
          <span>
            {dirtySections.length === 1
              ? `${dirtySections[0]!.nav} has unsaved changes`
              : `${dirtySections.length} sections have unsaved changes`}
          </span>
          <button
            type="button"
            className="btn-compact"
            disabled={saving}
            onClick={() => void saveSections(dirtySections)}
          >
            {saving ? (
              <T k="settings.saving" d="Saving…" />
            ) : (
              <T k="settings.action.saveAll" d="Save Everything" />
            )}
          </button>
        </div>
      )}
    </>
  );
}

/** One role's three editable lines. Placeholders show exactly what an empty
 *  field will send, so "blank" is never a mystery. */
function WelcomeFields({
  title,
  note,
  prefix,
  values,
  onChange,
}: {
  title: string;
  note: string;
  prefix: '' | 'staff_';
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  const key = (part: string) => `welcome_${prefix}${part}`;
  const def = (part: string) => DEFAULTS[key(part) as keyof typeof DEFAULTS];

  /* Whether anything here differs from the shipped copy. Drives the reset,
     which is only worth showing when there is something to undo. */
  const edited = ['subject', 'heading', 'intro'].some(
    (part) => (values[key(part)] ?? '') !== def(part),
  );

  return (
    <div className="wfields">
      <div className="wfields-head">
        <div>
          <p className="wfields-title">{title}</p>
          <p className="fdesc" style={{ marginTop: 0 }}>{note}</p>
        </div>
        {edited && (
          <button
            type="button"
            className="cedit"
            onClick={() => {
              for (const part of ['subject', 'heading', 'intro']) onChange(key(part), def(part));
            }}
          >
            Reset to Default
          </button>
        )}
      </div>

      <div className="fg">
        <label className="fl" htmlFor={key('subject')}>Subject Line</label>
        <input
          id={key('subject')}
          className="fi"
          maxLength={200}
          value={values[key('subject')] ?? ''}
          onChange={(e) => onChange(key('subject'), e.target.value)}
        />
      </div>

      <div className="fg">
        <label className="fl" htmlFor={key('heading')}>Heading</label>
        <input
          id={key('heading')}
          className="fi"
          maxLength={200}
          value={values[key('heading')] ?? ''}
          onChange={(e) => onChange(key('heading'), e.target.value)}
        />
      </div>

      <div className="fg">
        <label className="fl" htmlFor={key('intro')}>Opening Line</label>
        <textarea
          id={key('intro')}
          className="fi ft"
          rows={2}
          maxLength={600}
          value={values[key('intro')] ?? ''}
          onChange={(e) => onChange(key('intro'), e.target.value)}
        />
      </div>
    </div>
  );
}


/**
 * The save for one section, and the status of that section.
 *
 * DISABLED IS THE INFORMATION. A greyed button says this section has nothing
 * waiting, which is the question somebody actually has when they arrive back
 * at a pane they edited ten minutes ago. The single button this screen used to
 * have was always live and always wrote everything, so it could answer
 * nothing.
 */
function SectionSave({
  section,
  dirty,
  saving,
  onSave,
}: {
  section: Section;
  dirty: boolean;
  saving: boolean;
  onSave: (sections: Section[]) => Promise<void>;
}) {
  return (
    <div className="setsave">
      <button
        type="button"
        className="btn-compact"
        disabled={!dirty || saving}
        onClick={() => void onSave([section])}
      >
        {saving ? (
          <T k="settings.saving" d="Saving…" />
        ) : (
          <T k="settings.action.saveSection" d="Save This Section" />
        )}
      </button>
      <span className="setsave-state">
        {dirty ? (
          <T k="settings.state.unsaved" d="Unsaved changes" />
        ) : (
          <T k="settings.state.clean" d="Nothing to save" />
        )}
      </span>
    </div>
  );
}
